import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';

export interface ClientMessage {
  id: string;
  tenant_id: string;
  client_id: string;
  sender_type: 'admin' | 'client';
  message: string;
  is_read: boolean;
  created_at: string;
}

interface Props {
  tenantId: string;
  clientId: string;
  senderType: 'admin' | 'client';
  clientName?: string;
}

function timeLabel(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) {
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function MessageThread({ tenantId, clientId, senderType, clientName }: Props) {
  const [messages, setMessages] = useState<ClientMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, []);

  const fetchMessages = useCallback(async () => {
    const { data, error } = await supabase
      .from('client_messages')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('client_id', clientId)
      .order('created_at', { ascending: true });

    if (!error && data) {
      setMessages(data as ClientMessage[]);
      scrollToBottom();
    }
    setLoading(false);

    // Mark unread messages as read for this side
    if (data && data.length > 0) {
      const unread = data
        .filter((m) => !m.is_read && m.sender_type !== senderType)
        .map((m) => m.id);
      if (unread.length > 0) {
        await supabase
          .from('client_messages')
          .update({ is_read: true })
          .in('id', unread);
      }
    }
  }, [tenantId, clientId, senderType, scrollToBottom]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Realtime
  useEffect(() => {
    const channel = supabase
      .channel(`messages-${clientId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'client_messages',
          filter: `client_id=eq.${clientId}`,
        },
        (payload) => {
          const newMsg = payload.new as ClientMessage;
          setMessages((prev) => {
            if (prev.find((m) => m.id === newMsg.id)) return prev;
            return [...prev, newMsg];
          });
          scrollToBottom();
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [clientId, scrollToBottom]);

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    setText('');

    const { error } = await supabase.from('client_messages').insert({
      tenant_id: tenantId,
      client_id: clientId,
      sender_type: senderType,
      message: trimmed,
    });

    if (error) {
      setText(trimmed); // restore on error
    }
    setSending(false);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-0">
        {loading && (
          <div className="flex items-center justify-center py-10">
            <div className="w-5 h-5 border-2 border-[#C8A96E] border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {!loading && messages.length === 0 && (
          <div className="flex flex-col items-center gap-3 py-10">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/5 text-[#0f172a]/20">
              <i className="ri-chat-3-line text-2xl"></i>
            </div>
            <p className="text-slate-400 text-sm text-center">
              No messages yet.<br />
              {senderType === 'admin'
                ? 'Send a message to start the conversation.'
                : 'Send a message to your photographer.'}
            </p>
          </div>
        )}

        {messages.map((msg) => {
          const isMe = msg.sender_type === senderType;
          return (
            <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[78%] ${isMe ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                {/* Sender label */}
                <span className="text-[10px] text-slate-300 px-1">
                  {isMe ? 'You' : (msg.sender_type === 'admin' ? 'Studio' : (clientName ?? 'Client'))}
                </span>
                {/* Bubble */}
                <div
                  className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed break-words ${
                    isMe
                      ? 'bg-[#C8A96E] text-black rounded-tr-sm'
                      : 'bg-slate-100 text-slate-800 border border-slate-200 rounded-tl-sm'
                  }`}
                >
                  {msg.message}
                </div>
                {/* Timestamp */}
                <span className="text-[10px] text-[#0f172a]/20 px-1">{timeLabel(msg.created_at)}</span>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-slate-200 bg-slate-50/50">
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message… (Enter to send)"
            rows={1}
            maxLength={1000}
            className="flex-1 bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 resize-none transition-colors"
            style={{ minHeight: '40px', maxHeight: '120px' }}
          />
          <button
            onClick={handleSend}
            disabled={!text.trim() || sending}
            className="w-10 h-10 flex items-center justify-center bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-40 rounded-xl transition-colors cursor-pointer shrink-0"
          >
            {sending ? (
              <span className="w-4 h-4 border-2 border-black/40 border-t-black rounded-full animate-spin" />
            ) : (
              <i className="ri-send-plane-fill text-black text-sm"></i>
            )}
          </button>
        </div>
        <p className="text-[#0f172a]/15 text-[10px] mt-1.5 text-right">Shift+Enter for new line</p>
      </div>
    </div>
  );
}
